import { FC } from "react";
import { FieldArray, useFormikContext } from "formik";
import Button from "@blanket/components/buttons/Button";
import TooltippedRemoveButton from "@blanket/components/buttons/TooltippedRemoveButton";
import FormSection from "@blanket/components/formSections/FormSection";
import SubGroupPanel from "@blanket/components/formSections/SubGroupPanel";
import { TextFormField } from "@blanket/components/formFields/FormFields";
import { FieldGroup } from "@blanket/components/layout/FieldGroup";
import type { CompanyRegistrationValues } from "../model/registrationTypes";
import { emptyPNumber } from "../model/registrationInitialValues";
import { useCompanyNameLookup } from "@blanket/hooks/useCompanyNameLookup";
import { CompanyNameSuggestions } from "../components/CompanyNameSuggestions";
import { RegistrationDateDisclosureNotice } from "../components/RegistrationDateDisclosureNotice";
import { RegistrationRemarksField } from "../components/RegistrationRemarksField";
import { RegistrationContactFields } from "../components/RegistrationContactFields";

export const TerminationSection: FC = () => {
  const { values } = useFormikContext<CompanyRegistrationValues>();
  const termination = values.termination;
  const nameLookup = useCompanyNameLookup({
    name: "termination.name",
    cvr: "termination.cvr",
    pNumbers: "termination.pNumbers",
  });

  return (
    <FormSection
      id="section-termination"
      number={2}
      title="Stamoplysninger og ophørsdato"
      description="Oplysninger om virksomheden og ophørsdato."
    >
      <div className="d-flex flex-column gap-4">
        <CompanyNameSuggestions
          items={nameLookup.data}
          isLoading={nameLookup.isLoading}
          onSelect={nameLookup.select}
          onDismiss={nameLookup.dismiss}
        >
          <TextFormField
            name="termination.name"
            label="Navn på virksomhed"
            id="termination-input-name"
            isRequired
          />
        </CompanyNameSuggestions>

        <TextFormField
          name="termination.cvr"
          label="CVR-nummer"
          id="termination-input-cvr"
          type="number"
          isRequired
        />

        <SubGroupPanel
          className="blanket-participant-subsection"
          title="P-numre"
          description="Tilføj alle P-numre, der ophører."
        >
          <FieldArray name="termination.pNumbers">
            {({ push, remove }) => (
              <FieldGroup className="blanket-participant-list">
                {termination.pNumbers.map((pNumber, index) => (
                  <div
                    key={pNumber._rowId}
                    className="blanket-participant-card"
                  >
                    <TextFormField
                      name={`termination.pNumbers.${index}.value`}
                      label={`P-nummer ${index + 1}`}
                      id={`termination-input-p-number-${index}`}
                      type="number"
                      isRequired
                    />

                    <div className="mt-3 d-flex justify-content-end">
                      <TooltippedRemoveButton
                        onClick={() => remove(index)}
                        ariaLabel="Fjern P-nummer"
                        disabled={termination.pNumbers.length <= 1}
                        tooltipContent="Mindst ét P-nummer skal angives"
                      >
                        Fjern
                      </TooltippedRemoveButton>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  fullWidth={false}
                  onClick={() => push(emptyPNumber())}
                  aria-label="Tilføj P-nummer"
                >
                  Tilføj P-nummer
                </Button>
              </FieldGroup>
            )}
          </FieldArray>
        </SubGroupPanel>

        {/*
          Ændring 11 (ADV-293) ER dette felt — det hed "Mulighed for at tilføje
          tekst" og stod efter datoen. Omdøbt og flyttet, ikke suppleret: ophør
          skal fortsat kun have ét fritekstfelt.
        */}
        <RegistrationRemarksField
          namePrefix="termination"
          idPrefix="termination"
        />

        <TextFormField
          name="termination.terminationDate"
          label="Dato for ophør"
          id="termination-input-termination-date"
          type="date"
          isRequired
        />

        <RegistrationDateDisclosureNotice />

        <div className="blanket-two-col">
          <RegistrationContactFields
            namePrefix="termination"
            idPrefix="termination"
          />
        </div>
      </div>
    </FormSection>
  );
};
